import { useState, useEffect } from 'react';
import { useWallet } from '../../hooks/useWallet';
import { useModalAlert } from '../ModalAlert';

const STEP_CHOOSE = 0;
const STEP_AGENT = 1;
const STEP_BUILDER = 2;
const STEP_DONE = 3;

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).catch(() => {});
}

function setAgentPrivateKey(portfolioId, key, isTestnet) {
  const lsKey = `hyperliquid_agent_${isTestnet ? 'testnet' : 'mainnet'}_${portfolioId}`;
  try { localStorage.setItem(lsKey, key); } catch {}
}

// ─── Step 0: Choose paper portfolio ──────────────────────────────────────────
function StepChoose({ paperPortfolios, portfolioDetails, selectedId, onSelect, portfolioName, onNameChange, isSubmitting, error }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        Select a paper portfolio to graduate. Your agent, strategy, and prompts will be copied to a new live Hyperliquid portfolio.
      </p>

      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {paperPortfolios.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No paper portfolios found.</p>
        ) : (
          paperPortfolios.map((p) => {
            const detail = portfolioDetails?.get(p.id);
            const pnl = detail?.total_pnl_usd;
            const pnlPct = detail?.total_pnl_pct;
            const agentName = detail?.active_agent?.name || p.active_agent?.name || '—';
            const isSelected = p.id === selectedId;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  isSelected
                    ? 'border-cyan-500 bg-cyan-900/20'
                    : 'border-slate-700 bg-slate-800/60 hover:border-slate-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{p.name}</span>
                  {pnl != null && (
                    <span className={`text-xs font-mono ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} ({pnlPct != null ? `${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(1)}%` : ''})
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Agent: {agentName}</p>
              </button>
            );
          })
        )}
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">Live portfolio name</label>
        <input
          type="text"
          value={portfolioName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="My Live Portfolio"
          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded text-sm text-red-300">{error}</div>
      )}

      {isSubmitting && (
        <p className="text-xs text-slate-400 text-center">Creating live portfolio…</p>
      )}
    </div>
  );
}

// ─── Step 1: Agent wallet setup ───────────────────────────────────────────────
function StepAgent({ agentAddress, agentMode, onSetMode, importedKey, onImportedKeyChange, importError }) {
  const [showKey, setShowKey] = useState(false);
  const isTestnet = false;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        A dedicated <strong className="text-white">agent wallet</strong> controls trading on your behalf.
        You can use the one we generated, or supply your own private key.
      </p>

      <div className="flex rounded-lg overflow-hidden border border-slate-600">
        <button
          type="button"
          onClick={() => onSetMode('generated')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${
            agentMode === 'generated'
              ? 'bg-cyan-600 text-white'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          Use Generated Key
        </button>
        <button
          type="button"
          onClick={() => onSetMode('import')}
          className={`flex-1 py-2 text-sm font-medium transition-colors border-l border-slate-600 ${
            agentMode === 'import'
              ? 'bg-cyan-600 text-white'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          Import My Key
        </button>
      </div>

      {agentMode === 'generated' ? (
        <>
          <div className="p-4 bg-green-900/20 border border-green-700 rounded-md">
            <p className="text-xs font-medium text-green-400 mb-2 uppercase tracking-wide">Agent Address</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 block p-2 text-xs font-mono bg-slate-900 border border-green-800 text-slate-200 rounded overflow-x-auto">
                {agentAddress}
              </code>
              <button
                type="button"
                onClick={() => copyToClipboard(agentAddress)}
                className="flex-shrink-0 p-2 text-slate-400 hover:text-slate-200 transition-colors"
                title="Copy address"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-3 bg-yellow-900/20 border border-yellow-700 rounded-md">
            <p className="text-sm font-medium text-yellow-300 mb-1">Next Steps on Hyperliquid</p>
            <ol className="text-sm text-yellow-400 list-decimal list-inside space-y-1">
              <li>
                Go to{' '}
                <a
                  href={isTestnet ? 'https://app.hyperliquid-testnet.xyz' : 'https://app.hyperliquid.xyz'}
                  target="_blank" rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Hyperliquid {isTestnet ? 'Testnet' : 'Platform'}
                </a>
              </li>
              <li>Navigate to Account → Agent Trading</li>
              <li>Paste the agent address above and approve it</li>
              <li>Return here and click Next</li>
            </ol>
          </div>
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Paste a 0x-prefixed 32-byte private key (66 characters) for an agent wallet you already control.
          </p>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={importedKey}
              onChange={onImportedKeyChange}
              placeholder="0x…"
              className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm font-mono pr-10 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
              tabIndex={-1}
            >
              {showKey
                ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              }
            </button>
          </div>
          {importError && <p className="text-red-400 text-xs">{importError}</p>}
          <div className="flex items-start gap-2 bg-amber-900/10 border border-amber-600/30 rounded p-2.5">
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            <p className="text-xs text-amber-400">
              Stored only in your browser's local storage — never sent to any server.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Builder fee approval ─────────────────────────────────────────────
function StepBuilderFee({ config, approving, error, onApprove }) {
  const feeRate = config?.builderFeeRate || '0.0100%';
  return (
    <div className="space-y-4">
      <div className="p-4 bg-purple-900/20 border border-purple-700 rounded-md">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 mt-0.5 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-purple-200">Approve Katbot Automation Fee</h4>
            <p className="mt-1 text-sm text-purple-300">
              Sign one MetaMask message authorising Katbot to charge a maximum of{' '}
              <strong className="text-purple-100">{feeRate}</strong> per trade.
            </p>
            <ul className="mt-2 text-xs text-purple-400 space-y-1 list-disc list-inside">
              <li>No on-chain transaction — a single off-chain signature only</li>
              <li>Fee is capped at {feeRate} (1 basis point)</li>
              <li>You can revoke this at any time on Hyperliquid</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded text-sm text-red-300">{error}</div>
      )}

      <button
        type="button"
        onClick={onApprove}
        disabled={approving}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {approving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Waiting for signature…
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
            Sign with MetaMask
          </>
        )}
      </button>
    </div>
  );
}

// ─── Step 3: Done ─────────────────────────────────────────────────────────────
function StepDone({ portfolioName }) {
  return (
    <div className="flex flex-col items-center text-center space-y-4 py-4">
      <div className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-bold text-white">Your live portfolio is ready!</h3>
        {portfolioName && (
          <p className="text-sm text-slate-400 mt-1">
            <strong className="text-slate-200">{portfolioName}</strong> is now live on Hyperliquid.
          </p>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Your agent, strategy, and prompts have been copied. Start trading from your dashboard.
      </p>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  const steps = ['Choose', 'Agent', 'Builder Fee', 'Done'];
  return (
    <div className="flex items-center gap-1 mb-5">
      {steps.map((label, i) => (
        <div key={i} className="flex items-center gap-1 flex-1">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
            i < step ? 'bg-cyan-600 text-white' :
            i === step ? 'bg-cyan-500 text-white' :
            'bg-slate-700 text-slate-500'
          }`}>
            {i < step ? '✓' : i + 1}
          </div>
          <span className={`text-xs truncate ${i === step ? 'text-slate-200' : 'text-slate-500'}`}>{label}</span>
          {i < steps.length - 1 && <div className={`flex-1 h-px mx-1 ${i < step ? 'bg-cyan-700' : 'bg-slate-700'}`} />}
        </div>
      ))}
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────
export function GraduateToLiveWizard({ isOpen, onClose, paperPortfolios, portfolioDetails, config, onGraduated }) {
  const { isConnected, signer, network, jwtToken } = useWallet();
  const { showError } = useModalAlert();

  const [step, setStep] = useState(STEP_CHOOSE);

  // Step 0 state
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [portfolioName, setPortfolioName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Pre-generated agent
  const [preGenAgent, setPreGenAgent] = useState(null); // { privateKey, address }

  // Step 1 state
  const [agentMode, setAgentMode] = useState('generated');
  const [importedKey, setImportedKey] = useState('');
  const [importError, setImportError] = useState('');

  // Created portfolio (returned from POST /graduate)
  const [createdPortfolio, setCreatedPortfolio] = useState(null);

  // Step 2 state
  const [builderFeeApproving, setBuilderFeeApproving] = useState(false);
  const [builderFeeError, setBuilderFeeError] = useState(null);

  const isTestnet = config?.defaultHyperliquidTestnet === true;

  // Generate agent key when wizard opens
  useEffect(() => {
    if (!isOpen) return;
    const generate = async () => {
      try {
        const { ethers } = await import('ethers');
        const wallet = ethers.Wallet.createRandom();
        setPreGenAgent({ privateKey: wallet.privateKey, address: wallet.address });
      } catch (err) {
        console.error('Failed to generate agent key:', err);
      }
    };
    generate();
  }, [isOpen]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setStep(STEP_CHOOSE);
      setSelectedPortfolio(null);
      setPortfolioName('');
      setIsSubmitting(false);
      setSubmitError(null);
      setPreGenAgent(null);
      setAgentMode('generated');
      setImportedKey('');
      setImportError('');
      setCreatedPortfolio(null);
      setBuilderFeeError(null);
    }
  }, [isOpen]);

  const handleSelectPortfolio = (p) => {
    setSelectedPortfolio(p);
    setPortfolioName(p.name);
    setSubmitError(null);
  };

  // Step 0 → POST /graduate → Step 1
  const handleChooseContinue = async () => {
    if (!selectedPortfolio) { setSubmitError('Please select a portfolio.'); return; }
    if (!portfolioName.trim()) { setSubmitError('Please enter a name for the live portfolio.'); return; }
    if (!preGenAgent) { setSubmitError('Agent key not ready yet. Please wait a moment.'); return; }

    const agentKey = agentMode === 'import' ? importedKey.trim() : preGenAgent.privateKey;
    const agentAddress = agentMode === 'import'
      ? (() => {
          try {
            const { ethers } = require('ethers');
            return new ethers.Wallet(agentKey).address;
          } catch { return null; }
        })()
      : preGenAgent.address;

    if (!agentAddress) { setSubmitError('Invalid agent key.'); return; }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const apiBase = config?.apiBaseUrl || 'http://localhost:8000';
      const resp = await fetch(`${apiBase}/portfolio/${selectedPortfolio.id}/graduate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` },
        body: JSON.stringify({
          name: portfolioName.trim(),
          agent_private_key: agentKey,
          agent_address: agentAddress,
          is_testnet: isTestnet,
        }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${resp.status}`);
      }
      const portfolio = await resp.json();
      setCreatedPortfolio(portfolio);
      setAgentPrivateKey(portfolio.id, agentKey, isTestnet);
      setStep(STEP_AGENT);
    } catch (err) {
      setSubmitError(`Failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 1 → Step 2
  const handleAgentNext = () => {
    if (agentMode === 'import') {
      const key = importedKey.trim();
      if (!key) { setImportError('Please enter your agent private key.'); return; }
      if (!key.startsWith('0x') || key.length !== 66) {
        setImportError('Invalid format — expected a 0x-prefixed 32-byte hex string (66 chars).'); return;
      }
      if (createdPortfolio) setAgentPrivateKey(createdPortfolio.id, key, isTestnet);
      setImportError('');
    }
    setBuilderFeeError(null);
    setStep(STEP_BUILDER);
  };

  // Step 2 → EIP-712 sign + POST /approve-builder-fee → Step 3
  const handleApproveBuilderFee = async () => {
    if (!createdPortfolio || !signer) return;

    const builderAddress = config?.builderAddress || '0xDdf3A93522D503a43f802BeB4882BfF18D940a20';
    const feeRate = config?.builderFeeRate || '0.0100%';

    try {
      setBuilderFeeApproving(true);
      setBuilderFeeError(null);

      const nonce = Date.now();
      const isMainnet = !isTestnet;
      const walletChainId = network?.chainId ?? (isMainnet ? 42161 : 421614);
      const signatureChainId = '0x' + walletChainId.toString(16);

      const domain = {
        name: 'HyperliquidSignTransaction',
        version: '1',
        chainId: walletChainId,
        verifyingContract: '0x0000000000000000000000000000000000000000',
      };

      const types = {
        'HyperliquidTransaction:ApproveBuilderFee': [
          { name: 'hyperliquidChain', type: 'string' },
          { name: 'maxFeeRate', type: 'string' },
          { name: 'builder', type: 'address' },
          { name: 'nonce', type: 'uint64' },
        ],
      };

      const action = {
        type: 'approveBuilderFee',
        hyperliquidChain: isMainnet ? 'Mainnet' : 'Testnet',
        signatureChainId,
        maxFeeRate: feeRate,
        builder: builderAddress,
        nonce,
      };

      const rawSig = await signer._signTypedData(domain, types, {
        hyperliquidChain: action.hyperliquidChain,
        maxFeeRate: action.maxFeeRate,
        builder: action.builder,
        nonce: action.nonce,
      });

      const r = rawSig.slice(0, 66);
      const s = '0x' + rawSig.slice(66, 130);
      const v = parseInt(rawSig.slice(130, 132), 16);

      const apiBase = config?.apiBaseUrl || 'http://localhost:8000';
      const resp = await fetch(`${apiBase}/portfolio/${createdPortfolio.id}/approve-builder-fee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` },
        body: JSON.stringify({ action, signature: { r, s, v }, nonce }),
      });

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(`Approval failed: ${resp.status} ${errText}`);
      }

      setStep(STEP_DONE);
    } catch (err) {
      console.error('Builder fee approval error:', err);
      setBuilderFeeError(`Fee approval failed: ${err.message}. Please try again.`);
    } finally {
      setBuilderFeeApproving(false);
    }
  };

  const handleClose = () => {
    if (step === STEP_DONE && onGraduated) onGraduated();
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const stepTitles = ['Choose Portfolio', 'Agent Wallet', 'Approve Fee', 'Live!'];

  const effectiveAgentAddress = agentMode === 'import'
    ? undefined
    : preGenAgent?.address;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-lg bg-slate-900 rounded-xl shadow-2xl border border-slate-700 my-4 sm:my-0">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-700">
          <h3 className="text-base font-semibold text-white">{stepTitles[step]}</h3>
          <button
            onClick={handleClose}
            disabled={isSubmitting || builderFeeApproving}
            className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-40"
          >
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-2">
          <StepIndicator step={step} />

          {step === STEP_CHOOSE && (
            <StepChoose
              paperPortfolios={paperPortfolios}
              portfolioDetails={portfolioDetails}
              selectedId={selectedPortfolio?.id}
              onSelect={handleSelectPortfolio}
              portfolioName={portfolioName}
              onNameChange={setPortfolioName}
              isSubmitting={isSubmitting}
              error={submitError}
            />
          )}

          {step === STEP_AGENT && (
            <StepAgent
              agentAddress={effectiveAgentAddress}
              agentMode={agentMode}
              onSetMode={(m) => { setAgentMode(m); setImportError(''); }}
              importedKey={importedKey}
              onImportedKeyChange={(e) => { setImportedKey(e.target.value); setImportError(''); }}
              importError={importError}
            />
          )}

          {step === STEP_BUILDER && (
            <StepBuilderFee
              config={config}
              approving={builderFeeApproving}
              error={builderFeeError}
              onApprove={handleApproveBuilderFee}
            />
          )}

          {step === STEP_DONE && (
            <StepDone portfolioName={createdPortfolio?.name || portfolioName} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700 mt-4">
          <div>
            {step > STEP_CHOOSE && step < STEP_DONE ? (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                disabled={isSubmitting || builderFeeApproving}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                ← Back
              </button>
            ) : step !== STEP_DONE ? (
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-slate-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            ) : null}
          </div>

          <div>
            {step === STEP_CHOOSE && (
              <button
                type="button"
                onClick={handleChooseContinue}
                disabled={isSubmitting || !selectedPortfolio}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Creating…' : 'Continue →'}
              </button>
            )}

            {step === STEP_AGENT && (
              <button
                type="button"
                onClick={handleAgentNext}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md transition-colors"
              >
                Next →
              </button>
            )}

            {step === STEP_BUILDER && (
              <button
                type="button"
                onClick={() => setStep(STEP_DONE)}
                disabled={builderFeeApproving}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-md disabled:opacity-50 transition-colors border border-slate-600"
              >
                Skip for now
              </button>
            )}

            {step === STEP_DONE && (
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md transition-colors"
              >
                Go to Dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
